import { useEffect, useRef, useState } from 'react';
import { 
  MessageSquare, 
  Calendar, 
  FileText, 
  Users, 
  Zap, 
  Shield,
  Smartphone,
  BarChart3,
  Clock
} from 'lucide-react';

const features = [
  {
    icon: MessageSquare,
    title: 'Instant Email Responses',
    description: 'Automatically email applicants the moment they apply. No delays, no manual work.',
    color: 'bg-blue-500',
  },
  {
    icon: Calendar,
    title: 'Calendly Scheduling',
    description: 'Applicants book their own interview slots via Calendly. Your calendar stays full effortlessly.',
    color: 'bg-green-500',
  },
  {
    icon: FileText,
    title: 'Resume Viewer',
    description: 'View PDF resumes inline. No downloads, no tab switching. Everything in one place.',
    color: 'bg-purple-500',
  },
  {
    icon: Users,
    title: 'Applicant Management',
    description: 'Track every applicant in one place. Add notes, update statuses, and stay organized effortlessly.',
    color: 'bg-orange-500',
  },
  {
    icon: Zap,
    title: 'Auto-Replies',
    description: 'Smart responses for common questions. AI handles the back-and-forth for you.',
    color: 'bg-yellow-500',
  },
  {
    icon: Shield,
    title: 'Complete Privacy',
    description: 'Your data is yours alone. Fully encrypted, never shared or sold.',
    color: 'bg-red-500',
  },
  {
    icon: Smartphone,
    title: 'Works on Any Device',
    description: 'Respond to applicants from your phone, tablet, or desktop. Stay connected anywhere.',
    color: 'bg-indigo-500',
  },
  {
    icon: BarChart3,
    title: 'Hiring Analytics',
    description: 'Track response rates, time-to-hire, and pipeline health. Make data-driven decisions.',
    color: 'bg-pink-500',
  },
  {
    icon: Clock,
    title: '24/7 Availability',
    description: 'Applicants can schedule anytime, even at 2 AM. You wake up to a full calendar.',
    color: 'bg-teal-500',
  },
];

export default function Features() {
  const [visibleItems, setVisibleItems] = useState<Set<number>>(new Set());
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = Number(entry.target.getAttribute('data-index'));
          if (entry.isIntersecting) {
            setVisibleItems((prev) => new Set([...prev, index]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    itemRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section id="features" className="section-padding bg-white">
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-20">
          <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-4">
            Features
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Everything you need to{' '}
            <span className="gradient-text">hire faster</span>
          </h2>
          <p className="text-lg text-gray-600">
            From the moment someone applies to the day they start, iHireFast 
            automates the busywork so you can focus on finding the perfect fit.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              ref={(el) => { itemRefs.current[index] = el; }}
              data-index={index}
              className={`card p-6 lg:p-8 group transition-all duration-500 ${
                visibleItems.has(index) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 50}ms` }}
            >
              <div className={`w-12 h-12 ${feature.color} rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                <feature.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {feature.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
